/**
 * Integration Config API Client
 * Axios instance for per-provider integration config (credentials + config
 * endpoints of the integrations service).
 *
 * REMOTE mode routes through backoffice-api's admin proxy, which is
 * Harmony-tenant-only and injects x-tenant-id from the `tenant_id` query param
 * (so only Harmony employees can configure any tenant). LOCAL mode hits the
 * integrations service directly with an x-tenant-id header for dev. Callers pass
 * the target tenant per request via `tenantScope` (both query + header).
 *
 * Distinct from `integrations-client.ts`, which talks to the settings service
 * for integration definitions/instances.
 */

import axios from 'axios';
import { getSessionToken } from '@descope/react-sdk';
import { AppMode } from '@/lib/config/types';
import { routeToTenantCell } from '@/lib/cells/route-to-tenant-cell';
import { BACKOFFICE_SERVICE_PATH } from '@/lib/api/backoffice-client';
import { PRIMARY_API_BASE_URL } from '@/lib/config/api-base';

interface StoredConfig {
  mode?: AppMode;
  localBaseUrl?: string;
  [key: string]: unknown;
}

const getStoredConfig = (): StoredConfig | null => {
  try {
    const stored = localStorage.getItem('harmony-management-config');
    if (stored) {
      return JSON.parse(stored) as StoredConfig;
    }
  } catch (error) {
    console.error('Failed to load config from localStorage:', error);
  }
  return null;
};

export const integrationConfigClient = axios.create();

const INTEGRATION_CONFIG_SERVICE_PATH = `${BACKOFFICE_SERVICE_PATH}/proxy/integrations/api/v1`;

export const configureIntegrationConfigClient = (): void => {
  try {
    const config = getStoredConfig();
    const mode = config?.mode ?? AppMode.REMOTE;

    let baseUrl: string;
    if (mode === AppMode.LOCAL) {
      const localBaseUrl = config?.localBaseUrl ?? 'http://localhost:8000';
      baseUrl = `${localBaseUrl}/api/v1`;
    } else {
      // Route through backoffice-api's admin proxy: it is Harmony-tenant-only
      // (validate_management_tenant_id) and injects x-tenant-id from the
      // `tenant_id` query param, so cross-tenant writes are safe and gated.
      baseUrl = (import.meta.env.VITE_INTEGRATIONS_API_URL as string | undefined)
        ?? `${PRIMARY_API_BASE_URL}${INTEGRATION_CONFIG_SERVICE_PATH}`;
    }

    integrationConfigClient.defaults.baseURL = baseUrl;
    integrationConfigClient.interceptors.request.use(async (v) => {
      if (
        mode === AppMode.REMOTE &&
        (await routeToTenantCell(v, INTEGRATION_CONFIG_SERVICE_PATH))
      ) {
        return v;
      }

      try {
        if (mode === AppMode.REMOTE) {
          const token = getSessionToken();
          if (token) {
            v.headers.set('Authorization', `Bearer ${token}`);
          }
        }
        // Target tenant scoping (tenant_id query + x-tenant-id header) is set
        // per request by the caller, so it is left untouched here.
        if (import.meta.env.DEV && import.meta.env.VITE_FORCE_TENANT_ID) {
          v.headers.set('x-tenant-id', import.meta.env.VITE_FORCE_TENANT_ID);
        }
      } catch (error: unknown) {
        console.error('Failed to configure Integration Config request headers:', error);
      }
      return v;
    });

    console.log(`Integration Config client configured for ${mode} mode with baseURL: ${baseUrl}`);
  } catch (error) {
    console.error('Failed to configure Integration Config client:', error);
  }
};

configureIntegrationConfigClient();
